import React, { useMemo } from "react";
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, keyframes, useTheme } from "@mui/material/styles";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FlipToFrontOutlinedIcon from "@mui/icons-material/FlipToFrontOutlined";
import FlipToBackOutlinedIcon from "@mui/icons-material/FlipToBackOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import { buildLayerEntries } from "./skillAgentMoodboardTools";

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

export default function MoodboardLayersDrawer({
  open,
  onClose,
  page,
  selectedSlotIds = new Set(),
  selectedMarkId = null,
  onSelectSlot,
  onSelectMark,
  onBringForward,
  onSendBackward,
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const entries = useMemo(() => buildLayerEntries(page), [page]);
  const reversed = useMemo(() => [...entries].reverse(), [entries]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      transitionDuration={{ enter: 280, exit: 200 }}
      BackdropProps={{
        sx: {
          bgcolor: alpha("#0f172a", 0.28),
          backdropFilter: "blur(3px)",
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 340 },
          borderLeft: "1px solid",
          borderColor: alpha(accent, 0.18),
          backgroundImage: `linear-gradient(180deg, ${alpha(accent, 0.07)} 0%, ${theme.palette.background.paper} 28%)`,
          boxShadow: `-12px 0 40px ${alpha("#0f172a", 0.14)}`,
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.14),
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.12),
              color: accent,
              border: "1px solid",
              borderColor: alpha(accent, 0.2),
            }}
          >
            <LayersOutlinedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: accent,
                display: "block",
                lineHeight: 1.1,
              }}
            >
              Moodboard
            </Typography>
            <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2}>
              Capas
            </Typography>
          </Box>
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Cerrar"
          sx={{
            bgcolor: alpha(accent, 0.08),
            "&:hover": { bgcolor: alpha(accent, 0.16) },
          }}
        >
          <CloseOutlinedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <List dense sx={{ py: 1, px: 1 }}>
        {reversed.map((entry, idx) => {
          const selected =
            entry.kind === "slot"
              ? selectedSlotIds.has(entry.id)
              : String(selectedMarkId) === String(entry.id);
          return (
            <ListItemButton
              key={entry.key}
              selected={selected}
              onClick={() => {
                if (entry.kind === "slot") onSelectSlot?.(entry.id);
                else onSelectMark?.(entry.id);
              }}
              sx={{
                py: 1,
                px: 1.25,
                mb: 0.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: selected
                  ? alpha(accent, 0.35)
                  : alpha("#94a3b8", 0.18),
                bgcolor: selected ? alpha(accent, 0.12) : alpha("#fff", 0.4),
                animation: open
                  ? `${slideIn} 320ms ease ${Math.min(idx, 12) * 28}ms both`
                  : "none",
                transition:
                  "background-color 160ms ease, border-color 160ms ease, transform 160ms ease",
                "&:hover": {
                  bgcolor: selected ? alpha(accent, 0.16) : alpha(accent, 0.06),
                  transform: "translateX(-2px)",
                },
                "&.Mui-selected:hover": {
                  bgcolor: alpha(accent, 0.18),
                },
              }}
            >
              <ListItemText
                primary={entry.label}
                secondary={entry.kind === "slot" ? "Foto · casilla" : "Marca"}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: selected ? 800 : 650,
                  noWrap: true,
                }}
                secondaryTypographyProps={{
                  variant: "caption",
                  sx: { color: selected ? accent : "text.secondary" },
                }}
              />
              {entry.kind === "slot" ? (
                <Box sx={{ display: "flex", gap: 0.25 }}>
                  <IconButton
                    size="small"
                    title="Traer al frente"
                    onClick={(e) => {
                      e.stopPropagation();
                      onBringForward?.(entry.id);
                    }}
                    sx={{
                      color: accent,
                      bgcolor: alpha(accent, 0.08),
                      "&:hover": { bgcolor: alpha(accent, 0.16) },
                    }}
                  >
                    <FlipToFrontOutlinedIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Enviar atrás"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSendBackward?.(entry.id);
                    }}
                    sx={{
                      color: accent,
                      bgcolor: alpha(accent, 0.08),
                      "&:hover": { bgcolor: alpha(accent, 0.16) },
                    }}
                  >
                    <FlipToBackOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              ) : null}
            </ListItemButton>
          );
        })}
        {!entries.length ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 1.5, py: 2.5 }}
          >
            No hay capas en esta página.
          </Typography>
        ) : null}
      </List>
    </Drawer>
  );
}
